import { useState } from "react";
import { Search, X, Loader2 } from "lucide-react";
import api from "../../lib/api";

const field = "w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]";

// Search runs only on explicit submit (Enter / button). Picks up to `max` titles.
export default function TitlePicker({ picked, onChange, max = 3 }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/catalog/search?q=${encodeURIComponent(q.trim())}`);
      setResults((data.results || []).filter((r) => r.source !== "podcast").slice(0, 8));
    } catch { setResults([]); } finally { setBusy(false); }
  };
  const add = (r) => { if (picked.length < max && !picked.some((p) => p.id === r.id)) onChange([...picked, { id: r.id, title: r.title, poster: r.poster || r.img }]); };
  return (
    <div data-testid="episode-title-picker">
      {picked.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {picked.map((p) => (
            <span key={p.id} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-accent)] bg-[var(--accent)]/10 py-1 pl-3 pr-1.5 text-xs font-semibold text-[var(--text-primary)]" data-testid={`episode-picked-${p.id}`}>
              {p.title}
              <button type="button" onClick={() => onChange(picked.filter((x) => x.id !== p.id))} aria-label={`Remove ${p.title}`} data-testid={`episode-picked-remove-${p.id}`} className="rounded-full p-0.5 hover:bg-black/30"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      {picked.length < max && (
        <div className="flex gap-2">
          <input className={field} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); run(); } }} placeholder="Search a film or show…" data-testid="episode-title-search" />
          <button type="button" onClick={run} className="inline-flex flex-none items-center gap-1 rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-3 text-sm font-semibold text-[var(--text-primary)] hover:border-[var(--accent)]" data-testid="episode-title-search-btn">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Search
          </button>
        </div>
      )}
      {results && picked.length < max && (
        <ul className="mt-2 max-h-64 overflow-y-auto rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)]" data-testid="episode-title-results">
          {results.length === 0 && <li className="p-3 text-sm text-[var(--text-secondary)]">No titles found.</li>}
          {results.filter((r) => !picked.some((p) => p.id === r.id)).map((r) => (
            <li key={r.id}>
              <button type="button" onClick={() => add(r)} className="flex w-full items-center gap-3 p-2 text-left hover:bg-[var(--accent)]/10" data-testid={`episode-title-result-${r.id}`}>
                {(r.poster || r.img) ? <img src={r.poster || r.img} alt="" className="h-12 w-8 flex-none rounded object-cover" /> : <span className="h-12 w-8 flex-none rounded bg-[var(--bg-base)]" />}
                <span className="min-w-0 text-sm text-[var(--text-primary)]"><span className="block truncate font-semibold">{r.title}</span><span className="text-xs text-[var(--text-muted)]">{[r.year, r.media_type === "tv" ? "Series" : "Film"].filter(Boolean).join(" · ")}</span></span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
